import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentContainer } from '../components/payments';
import { useCart } from '../contexts/CartContext';
import authService from '../services/authService';
import analyticsService, { COMMERCE_SOURCES } from '../services/analyticsService';
import './CheckoutPage.css';

const API_URL = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (window.location.origin.includes('localhost')) return 'http://localhost:8080/api';
  return `${window.location.origin}/api`;
})();

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items: cartItems, getTotalPrice, clearCart } = useCart();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('shipping'); // shipping | payment

  // Shipping config from backend (used to auto-calculate domicilio cost)
  const [shippingConfig, setShippingConfig] = useState([]);
  const [shippingMode, setShippingMode] = useState(null); // 'gratis' | 'domicilio'
  const [shippingCost, setShippingCost] = useState(0);
  const [matchedOption, setMatchedOption] = useState(null);

  // Guest info form (name + email for non-logged-in users)
  const [guestForm, setGuestForm] = useState({ nombre: '', email: '' });

  // Shipping address form
  const [shippingForm, setShippingForm] = useState({
    direccion: '',
    ciudad: '',
    departamento: '',
    telefono: '',
    notas: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponData, setCouponData] = useState(null); // { valid, discount_amount, discount_type, discount_value, name, message }
  const [couponError, setCouponError] = useState('');

  const subtotal = getTotalPrice();
  const discountAmount = couponData?.valid ? couponData.discount_amount : 0;
  const totalAmount = subtotal + shippingCost - discountAmount;
  // Send undiscounted amount to backend — backend applies coupon discount server-side
  const paymentAmount = subtotal + shippingCost;

  // Firma del carrito + envío: identifica el estado contra el que se validó el
  // cupón aplicado (ver el efecto que lo invalida más abajo).
  const cartSignature = `${cartItems.map(i => `${i.productId}:${i.quantity}`).join('|')}#${shippingCost}`;
  const appliedSignature = useRef(null);

  // Refs for abandonment tracking — keep latest step/value accessible in cleanup
  const paymentCompleted = useRef(false);
  const stepRef = useRef(step);
  const subtotalRef = useRef(subtotal);
  const cartReached = useRef(false);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { subtotalRef.current = subtotal; }, [subtotal]);

  // Load user and shipping options
  useEffect(() => {
    const currentUser = authService.getUser();
    setUser(currentUser);

    if (cartItems.length === 0) {
      navigate('/carrito');
      return;
    }

    // Pre-fill guest fields from logged-in user
    if (currentUser) {
      setGuestForm(prev => ({
        ...prev,
        nombre: currentUser.name || '',
        email: currentUser.email || '',
      }));
    }

    // Fetch shipping config for auto-calculation
    fetch(`${API_URL}/envios/opciones`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setShippingConfig(data);
          // Auto-select shipping mode based on available options
          const hasPickup = data.some(o => o.costo === 0);
          const hasDelivery = data.some(o => o.costo > 0);
          if (hasDelivery && !hasPickup) {
            setShippingMode('domicilio');
          } else if (hasPickup && !hasDelivery) {
            setShippingMode('gratis');
          }
        }
      })
      .catch(err => console.error('Error loading shipping options:', err))
      .finally(() => setLoading(false));
  }, [cartItems, navigate]);

  // Track checkout reach + abandonment on unmount
  useEffect(() => {
    if (cartItems.length === 0) return;
    cartReached.current = true;
    analyticsService.trackEvent('checkout', 'reach', 'page_view', null, {
      items_count: cartItems.length,
      cart_value: subtotalRef.current,
    });
    return () => {
      if (cartReached.current && !paymentCompleted.current) {
        analyticsService.trackEvent('checkout', 'abandon', stepRef.current, null, {
          step: stepRef.current,
          cart_value: subtotalRef.current,
        });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate shipping cost based on city + mode
  useEffect(() => {
    if (shippingMode !== 'domicilio') {
      setShippingCost(0);
      setMatchedOption(null);
      return;
    }
    const ciudad = shippingForm.ciudad.trim().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // remove accents
    // Try to match a city-specific option
    let matched = shippingConfig.find(o => {
      const optName = (o.nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return ciudad && optName.includes(ciudad) && o.costo > 0;
    });
    // Fallback to "nacional" option
    if (!matched) {
      matched = shippingConfig.find(o => {
        const optName = (o.nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return optName.includes('nacional');
      });
    }
    if (matched) {
      setMatchedOption(matched);
      if (matched.es_gratis_desde && subtotal >= matched.es_gratis_desde) {
        setShippingCost(0);
      } else {
        setShippingCost(matched.costo);
      }
    } else {
      setMatchedOption(null);
      setShippingCost(0);
    }
  }, [shippingMode, shippingForm.ciudad, subtotal, shippingConfig]);

  const validateShippingForm = () => {
    const errors = {};
    if (!user) {
      if (!guestForm.nombre.trim()) errors.nombre = 'El nombre es obligatorio';
      if (!guestForm.email.trim()) errors.email = 'El correo electrónico es obligatorio';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestForm.email)) errors.email = 'El correo no es válido';
    }
    if (!shippingForm.direccion.trim()) errors.direccion = 'La dirección es obligatoria';
    if (!shippingForm.ciudad.trim()) errors.ciudad = 'La ciudad es obligatoria';
    if (!shippingForm.departamento.trim()) errors.departamento = 'El departamento es obligatorio';
    if (!shippingForm.telefono.trim()) errors.telefono = 'El teléfono de contacto es obligatorio';
    if (!shippingMode) errors.shipping = 'Selecciona una opción de envío';
    if (shippingMode === 'domicilio' && !shippingForm.direccion.trim()) errors.direccion = 'La dirección es obligatoria para envío a domicilio';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinueToPayment = () => {
    if (validateShippingForm()) {
      setStep('payment');
      analyticsService.trackCommerce('start_checkout', null, cartItems?.length || 0, totalAmount, COMMERCE_SOURCES.CHECKOUT);
    } else {
      const errorFields = Object.keys(formErrors);
      analyticsService.trackEvent('checkout', 'form_error', 'shipping_validation', errorFields.join(','), {
        fields: errorFields,
        cart_value: subtotal,
      });
    }
  };

  const handlePaymentSuccess = result => {
    paymentCompleted.current = true;
    analyticsService.trackCommerce('complete_checkout', null, cartItems?.length || 0, totalAmount, COMMERCE_SOURCES.CHECKOUT);
    clearCart();
    alert('¡Pago procesado exitosamente! Recibirás un email de confirmación.');
  };

  const handlePaymentError = error => {
    analyticsService.trackEvent('checkout', 'payment_error', 'failed', String(error), {
      cart_value: totalAmount,
    });
    console.error('Payment error:', error);
    alert(`Error en el pago: ${error}`);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponData(null);
    try {
      const token = authService.getToken();
      const response = await fetch(`${API_URL}/coupons/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        // Los ítems son necesarios para los cupones atados a un producto: el
        // backend valida contra ellos que el producto esté en el carrito y
        // calcula el descuento solo sobre esa línea.
        body: JSON.stringify({
          code: couponCode.trim(),
          subtotal,
          shipping_cost: shippingCost,
          items: cartItems
            .filter(item => item.productId)
            .map(item => ({
              producto_id: parseInt(item.productId),
              cantidad: item.quantity,
              precio: item.price,
            })),
        }),
      });
      const data = await response.json();
      if (data.valid) {
        setCouponData(data);
        appliedSignature.current = cartSignature;
      } else {
        setCouponError(data.message || 'Cupón no válido');
        setCouponData(null);
      }
    } catch (err) {
      setCouponError('Error al validar el cupón');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponData(null);
    setCouponError('');
    appliedSignature.current = null;
  };

  // El descuento se calculó contra el carrito que había al validar el cupón.
  // Si después cambia el carrito o el envío, ese descuento deja de ser válido
  // —un cupón de un solo producto se rechaza si ese producto sale del
  // carrito— así que se limpia y se pide volver a aplicarlo, en vez de dejar
  // que el pago falle al final con un error de la pasarela.
  useEffect(() => {
    if (appliedSignature.current && appliedSignature.current !== cartSignature) {
      appliedSignature.current = null;
      setCouponData(null);
      setCouponError('El carrito cambió. Vuelve a aplicar el cupón.');
    }
  }, [cartSignature]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Finalizar Compra</h1>
          {user && (
            <div className="user-info">
              <span>👤 {user.name}</span>
              <span>📧 {user.email}</span>
            </div>
          )}
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            <span
              style={{ cursor: 'pointer', fontWeight: step === 'shipping' ? 'bold' : 'normal', color: step === 'shipping' ? 'var(--color-accent-light)' : 'var(--color-muted)' }}
              onClick={() => setStep('shipping')}
            >
              1. Envío
            </span>
            <span style={{ color: 'var(--color-muted)' }}>→</span>
            <span style={{ fontWeight: step === 'payment' ? 'bold' : 'normal', color: step === 'payment' ? 'var(--color-accent-light)' : 'var(--color-muted)' }}>
              2. Pago
            </span>
          </div>
        </div>

        <div className="checkout-content">
          {/* Order Summary - always visible */}
          <div className="checkout-summary">
            <h2>Resumen de tu Pedido</h2>
            <div className="cart-items">
              {cartItems.map(item => (
                <div key={item.id} className="cart-item">
                  <div className="item-image">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="item-details">
                    <h4>{item.name}</h4>
                    <p>Cantidad: {item.quantity}</p>
                    <p className="item-price">{formatCurrency(item.price * item.quantity)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Coupon input — only for authenticated users */}
            {user && (
              <div style={{ margin: '1rem 0', padding: '0.8rem', background: 'var(--color-surface-2)', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.4rem', fontSize: '0.85rem', color: 'var(--color-text)' }}>🎟️ ¿Tienes un cupón de descuento?</label>
                {couponData?.valid ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-success-soft)', padding: '0.5rem 0.8rem', borderRadius: '6px', border: '1px solid var(--color-success)' }}>
                    <div>
                      <span style={{ fontWeight: 'bold', color: 'var(--color-success)', fontSize: '0.9rem' }}>✅ {couponData.name}</span>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: 'var(--color-success)' }}>(-{formatCurrency(couponData.discount_amount)})</span>
                      {couponData.producto_nombre && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.2rem 0 0' }}>
                          Aplica solo a {couponData.producto_nombre}
                        </p>
                      )}
                    </div>
                    <button onClick={handleRemoveCoupon} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>Quitar</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                      placeholder="Ingresa tu código"
                      style={{ flex: 1, padding: '0.5rem 0.7rem', borderRadius: '6px', border: couponError ? '1px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.9rem', fontFamily: 'monospace', textTransform: 'uppercase' }}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: 'var(--color-accent-strong)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', opacity: couponLoading || !couponCode.trim() ? 0.6 : 1 }}
                    >
                      {couponLoading ? '...' : 'Aplicar'}
                    </button>
                  </div>
                )}
                {couponError && <p style={{ color: 'var(--color-danger)', fontSize: '0.8rem', marginTop: '0.3rem' }}>{couponError}</p>}
              </div>
            )}

            <div className="order-total">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="total-row">
                <span>Envío:</span>
                <span style={{ color: shippingCost === 0 ? 'var(--color-success)' : 'inherit' }}>
                  {shippingCost === 0 ? 'Gratis' : formatCurrency(shippingCost)}
                </span>
              </div>
              {discountAmount > 0 && (
                <div className="total-row" style={{ color: 'var(--color-success)' }}>
                  <span>Descuento ({couponData?.code}):</span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              <div className="total-row final-total">
                <span>Total:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Step content */}
          {step === 'shipping' ? (
            <div className="payment-section">
              <div style={{ background: 'var(--color-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                <h2 style={{ marginBottom: '1rem', fontSize: '1.3rem', color: 'var(--color-text)' }}>📦 Dirección de Envío</h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {/* Guest info fields — only for non-logged-in users */}
                  {!user && (
                    <>
                      <div style={{ background: 'var(--color-accent-soft)', border: '1px solid var(--color-accent)', borderRadius: '8px', padding: '0.8rem 1rem', fontSize: '0.85rem', color: 'var(--color-accent-light)' }}>
                        Compra sin crear cuenta. También puedes{' '}
                        <a href="/login" style={{ color: 'var(--color-accent-light)', fontWeight: '600', textDecoration: 'underline' }}>iniciar sesión</a>
                        {' '}para ver tu historial de pedidos.
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Nombre completo *</label>
                          <input
                            type="text"
                            value={guestForm.nombre}
                            onChange={e => { setGuestForm(p => ({ ...p, nombre: e.target.value })); setFormErrors(p => ({ ...p, nombre: '' })); }}
                            placeholder="Tu nombre"
                            style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.nombre ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)' }}
                          />
                          {formErrors.nombre && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.nombre}</span>}
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Correo electrónico *</label>
                          <input
                            type="email"
                            value={guestForm.email}
                            onChange={e => { setGuestForm(p => ({ ...p, email: e.target.value })); setFormErrors(p => ({ ...p, email: '' })); }}
                            placeholder="Para el comprobante"
                            style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.email ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)' }}
                          />
                          {formErrors.email && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.email}</span>}
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Dirección *</label>
                    <input
                      type="text"
                      name="direccion"
                      value={shippingForm.direccion}
                      onChange={handleInputChange}
                      placeholder="Ej: Calle 100 #15-20 Apto 301"
                      style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.direccion ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)' }}
                    />
                    {formErrors.direccion && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.direccion}</span>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Ciudad *</label>
                      <input
                        type="text"
                        list="ciudades-colombia"
                        name="ciudad"
                        value={shippingForm.ciudad}
                        onChange={handleInputChange}
                        placeholder="Escribe o selecciona tu ciudad"
                        autoComplete="off"
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.ciudad ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)', background: 'var(--color-surface-2)' }}
                      />
                      <datalist id="ciudades-colombia">
                        {[
                          'Bogotá','Cali','Barranquilla','Cartagena','Cúcuta','Bucaramanga','Pereira','Santa Marta',
                          'Ibagué','Pasto','Manizales','Neiva','Villavicencio','Armenia','Valledupar','Montería','Sincelejo',
                          'Popayán','Tunja','Riohacha','Quibdó','Florencia','Yopal','Mocoa','Arauca','Leticia','Mitú',
                          'Puerto Carreño','San José del Guaviare','Inírida','San Andrés','Providencia',
                          'Soledad','Bello','Soacha','Itagüí','Envigado','Buenaventura','Barrancabermeja','Palmira',
                          'Dosquebradas','Tuluá','Sogamoso','Duitama','Girardot','Maicao','Apartadó','Turbo',
                          'Magangué','Fusagasugá','Facatativá','Zipaquirá','Chía','Girón','Piedecuesta','Floridablanca',
                          'Tumaco','Cartago','Buga','Jamundí','Rionegro','La Dorada','Honda','Espinal','Melgar',
                          'Sahagún','Lorica','Cereté','Ciénaga','Fundación','Aguachica','Ocaña','Pamplona',
                          'Ipiales','Túquerres','La Unión','El Carmen de Viboral','Marinilla','La Ceja','Copacabana',
                          'Sabaneta','Caldas','La Estrella','Barbosa','Puerto Boyacá','Chiquinquirá','Paipa',
                          'Villa de Leyva','Moniquirá','Garzón','Pitalito','La Plata','Campoalegre',
                          'Acacías','Granada','San Martín','Puerto López','Aguazul','Tauramena','Paz de Ariporo',
                          'Chinchiná','Villamaría','Neira','Riosucio','La Virginia','Marsella','Santa Rosa de Cabal',
                          'Calarcá','Circasia','Montenegro','La Tebaida','Quimbaya','Líbano','Mariquita','Lérida',
                          'Chaparral','Cajamarca','Tocancipá','Sopó','Tabio','Tenjo','Cota','La Calera','Cajicá',
                          'Mosquera','Madrid','Funza','Sibaté','Silvania','Arbeláez','Chocontá','Ubaté',
                        ].map(c => <option key={c} value={c} />)}
                      </datalist>
                      {formErrors.ciudad && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.ciudad}</span>}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Departamento *</label>
                      <input
                        type="text"
                        name="departamento"
                        value={shippingForm.departamento}
                        onChange={handleInputChange}
                        placeholder="Ej: Cundinamarca"
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.departamento ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)' }}
                      />
                      {formErrors.departamento && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.departamento}</span>}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Teléfono de contacto *</label>
                    <input
                      type="tel"
                      name="telefono"
                      value={shippingForm.telefono}
                      onChange={handleInputChange}
                      placeholder="Ej: 3001234567"
                      style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: formErrors.telefono ? '2px solid var(--color-danger)' : '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--color-text)' }}
                    />
                    {formErrors.telefono && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem' }}>{formErrors.telefono}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.3rem', fontSize: '0.9rem', color: 'var(--color-text)' }}>Notas de envío (opcional)</label>
                    <textarea
                      name="notas"
                      value={shippingForm.notas}
                      onChange={handleInputChange}
                      placeholder="Instrucciones especiales para la entrega..."
                      rows={2}
                      style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '0.95rem', resize: 'vertical', color: 'var(--color-text)' }}
                    />
                  </div>
                </div>

                <h3 style={{ marginTop: '1.5rem', marginBottom: '0.8rem', fontSize: '1.1rem', color: 'var(--color-text)' }}>🚚 Tipo de Entrega *</h3>
                {formErrors.shipping && <span style={{ color: 'var(--color-danger)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>{formErrors.shipping}</span>}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {/* Free pickup option - only if returned by API (activo) */}
                  {shippingConfig.some(o => o.costo === 0) && (
                    <div
                      onClick={() => { setShippingMode('gratis'); setFormErrors(prev => ({ ...prev, shipping: '' })); }}
                      style={{
                        padding: '0.8rem 1rem',
                        borderRadius: '8px',
                        border: shippingMode === 'gratis' ? '2px solid var(--color-accent)' : '1px solid var(--border-color)',
                        background: shippingMode === 'gratis' ? 'var(--color-accent-soft)' : 'var(--color-surface-2)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: '0.95rem' }}>🏪 {shippingConfig.find(o => o.costo === 0)?.nombre || 'Recogida en tienda'}</strong>
                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: 'var(--color-muted)' }}>{shippingConfig.find(o => o.costo === 0)?.descripcion || 'Recoge tu pedido sin costo adicional'}</p>
                      </div>
                      <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>Gratis</span>
                    </div>
                  )}

                  {/* Home delivery option - only if there are delivery options with cost > 0 */}
                  {shippingConfig.some(o => o.costo > 0) && (
                    <div
                      onClick={() => { setShippingMode('domicilio'); setFormErrors(prev => ({ ...prev, shipping: '' })); }}
                      style={{
                        padding: '0.8rem 1rem',
                        borderRadius: '8px',
                        border: shippingMode === 'domicilio' ? '2px solid var(--color-accent)' : '1px solid var(--border-color)',
                        background: shippingMode === 'domicilio' ? 'var(--color-accent-soft)' : 'var(--color-surface-2)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: '0.95rem' }}>🚚 Envío a domicilio</strong>
                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                          El costo se calcula según tu ciudad
                        </p>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        {shippingMode === 'domicilio' && matchedOption ? (
                          shippingCost === 0 ? (
                            <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>Gratis</span>
                          ) : (
                            <span style={{ fontWeight: 'bold' }}>{formatCurrency(shippingCost)}</span>
                          )
                        ) : (
                          <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Según ciudad</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Show matched shipping info when domicilio selected */}
                {shippingMode === 'domicilio' && matchedOption && (
                  <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.8rem', background: 'var(--color-success-soft)', borderRadius: '8px', fontSize: '0.8rem', color: 'var(--color-success)' }}>
                    📍 <strong>{matchedOption.nombre}</strong>: {shippingCost === 0 ? '¡Envío gratis!' : formatCurrency(matchedOption.costo)}
                    {matchedOption.es_gratis_desde && shippingCost > 0 && (
                      <span> · Gratis en compras desde {formatCurrency(matchedOption.es_gratis_desde)}</span>
                    )}
                  </div>
                )}
                {shippingMode === 'domicilio' && !shippingForm.ciudad.trim() && (
                  <p style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--color-warning)' }}>Ingresa tu ciudad arriba para calcular el costo de envío</p>
                )}

                <button
                  onClick={handleContinueToPayment}
                  style={{
                    marginTop: '1.5rem',
                    width: '100%',
                    padding: '0.9rem',
                    borderRadius: '8px',
                    background: 'var(--color-accent)',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Continuar al Pago →
                </button>
              </div>
            </div>
          ) : (
            <div className="payment-section">
              {/* Show shipping summary */}
              <div className="checkout-shipping-summary">
                <strong>📦 Envío a:</strong> {shippingForm.direccion}, {shippingForm.ciudad}, {shippingForm.departamento}
                <br />
                <strong>📞</strong> {shippingForm.telefono}
                <button onClick={() => setStep('shipping')} style={{ marginLeft: '0.5rem', color: 'var(--color-accent-light)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
                  Cambiar
                </button>
              </div>
              <PaymentContainer
                amount={paymentAmount}
                displayAmount={totalAmount}
                products={cartItems}
                shippingData={{
                  direccion_envio: shippingMode === 'domicilio' ? shippingForm.direccion : (shippingConfig.find(o => o.costo === 0)?.nombre || 'Recogida en tienda'),
                  ciudad: shippingForm.ciudad,
                  departamento: shippingForm.departamento,
                  telefono_contacto: shippingForm.telefono,
                  notas_envio: shippingForm.notas,
                  costo_envio: shippingCost,
                  envio_opcion_id: matchedOption?.id || 0,
                }}
                couponCode={couponData?.valid ? couponData.code : ''}
                guestInfo={!user ? { nombre: guestForm.nombre, email: guestForm.email } : null}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            </div>
          )}

          {/* Trust indicators - aligned with right column */}
          <div></div>{/* empty spacer for left column */}
          <div className="trust-indicators">
            <div className="trust-item">
              <span className="trust-icon">🔒</span>
              <span>Pagos Seguros</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🚚</span>
              <span>Envío a toda Colombia</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">↩️</span>
              <span>Devoluciones</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">⭐</span>
              <span>Garantía</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
